import { useCallback, useEffect, useState } from "react";
import { getParentDetail } from "../lib/admin.js";
import AdminSidebar from "./AdminSidebar.jsx";

function formatDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatAge(birthMonth) {
  if (!birthMonth) return "";
  const b = new Date(birthMonth);
  const n = new Date();
  const months = (n.getFullYear() - b.getFullYear()) * 12 + (n.getMonth() - b.getMonth());
  if (months < 12) return `${Math.max(0, months)}개월`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem === 0 ? `${years}세` : `${years}세 ${rem}개월`;
}

function formatKRW(n) {
  if (n == null) return "-";
  return n.toLocaleString() + "원";
}

const STATUS_LABEL = {
  draft: "작성 중",
  received: "신규 · 우선",
  analyzing: "분석 중",
  research: "AI 리서치 중",
  reviewing: "검토 대기",
  done: "전송 완료",
  cancelled: "취소됨",
};

const STATUS_TONE = {
  draft:    ["#a0aec022", "#4a5568"],
  received: ["#e74c3c22", "#c0392b"],
  analyzing:["#ff993322", "#e6852b"],
  research: ["#ff993322", "#e6852b"],
  reviewing:["#2c5f8d22", "#2c5f8d"],
  done:     ["#27ae6022", "#1f7a3f"],
  cancelled:["#a0aec022", "#4a5568"],
};

const PURCHASE_STATUS_LABEL = {
  pending: "결제 대기",
  paid: "결제 완료",
  failed: "실패",
  refunded: "환불",
};

const PURCHASE_STATUS_TONE = {
  pending: ["#ff993322", "#e6852b"],
  paid:    ["#27ae6022", "#1f7a3f"],
  failed:  ["#e74c3c22", "#c0392b"],
  refunded:["#a0aec022", "#4a5568"],
};

export default function AdminParentDetail({ userId, onBack, onSignOut }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await getParentDetail(userId);
      if (!d) {
        setError("보호자를 찾을 수 없어요.");
        return;
      }
      setData(d);
    } catch (err) {
      setError(err.message ?? "불러오기 실패");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="admin">
        <AdminSidebar active="parents" onSignOut={onSignOut}/>
        <main className="admin-main">
          <button onClick={onBack} className="btn btn-outline" style={{padding:'6px 12px',fontSize:12,marginBottom:14}}>
            ← 보호자 목록
          </button>
          {error ? (
            <div style={{background:'#fef9f0',border:'1px solid #e74c3c44',borderRadius:12,padding:'14px 16px',color:'#c0392b',fontSize:13}}>
              {error}
            </div>
          ) : (
            <div style={{textAlign:'center',padding:'80px 0'}}>
              <div className="spinner" style={{display:'inline-block'}}/>
            </div>
          )}
        </main>
      </div>
    );
  }

  const { user, children, cases, purchases, credits, notifications } = data;

  const now = Date.now();
  const activeCredits = credits.filter((p) => !p.expires_at || new Date(p.expires_at).getTime() > now);
  const totalCreditsRemaining = activeCredits.reduce((acc, p) => acc + (p.credits_remaining ?? 0), 0);
  const paid = purchases.filter((p) => p.status === "paid");
  const totalPaid = paid.reduce((acc, p) => acc + (p.amount_won ?? 0), 0);
  const doneCases = cases.filter((c) => c.status === "done");
  const refundedCount = purchases.filter((p) => p.status === "refunded").length;

  return (
    <div className="admin">
      <AdminSidebar active="parents" onSignOut={onSignOut}/>

      <main className="admin-main" style={{paddingRight:28}}>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:6,flexWrap:'wrap'}}>
          <button onClick={onBack} className="btn btn-outline" style={{padding:'6px 12px',fontSize:12}}>
            ← 보호자 목록
          </button>
          <h1 className="admin-h1" style={{margin:0}}>
            {user.display_name || user.email}
          </h1>
        </div>
        <p className="admin-sub" style={{marginBottom:18}}>
          {user.email}
          {" · 가입 "}{formatDate(user.created_at)}
        </p>

        {/* KPI 카드 */}
        <div className="admin-stats" style={{gridTemplateColumns:'repeat(4, 1fr)'}}>
          <Stat label="자녀" value={children.length} tone="#2c5f8d"/>
          <Stat label="발행된 리포트" value={doneCases.length} sub={`전체 ${cases.length}건 중`} tone="#1f7a3f"/>
          <Stat label="잔여 분석권" value={`🎟 ${totalCreditsRemaining}`} tone="#6c5ce7"/>
          <Stat label="누적 결제" value={formatKRW(totalPaid)} sub={refundedCount > 0 ? `환불 ${refundedCount}건` : null} tone="#e6852b"/>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'minmax(0, 1.4fr) minmax(0, 1fr)',gap:18,alignItems:'flex-start'}}>
          <div style={{minWidth:0}}>
            {/* 자녀 */}
            <Section title={`👶 자녀 ${children.length}명`}>
              {children.length === 0 ? (
                <div style={{fontSize:12,color:'#718096'}}>등록된 자녀가 없어요.</div>
              ) : (
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  {children.map((ch) => (
                    <div key={ch.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',background:'#fafafa',borderRadius:10}}>
                      <div style={{width:36,height:36,borderRadius:18,background:'#e2e8f0',overflow:'hidden',flexShrink:0,
                        display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>
                        {ch.avatar_url ? <img src={ch.avatar_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/> : '👶'}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:13}}>
                          {ch.name}
                          {formatAge(ch.birth_month) && (
                            <span style={{color:'#718096',fontWeight:500,marginLeft:6}}>· {formatAge(ch.birth_month)}</span>
                          )}
                        </div>
                        <div style={{fontSize:11,color:'#a0aec0'}}>등록 {formatDate(ch.created_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* 케이스 목록 */}
            <Section title={`📋 케이스 ${cases.length}건`}>
              {cases.length === 0 ? (
                <div style={{fontSize:12,color:'#718096'}}>아직 등록된 케이스가 없어요.</div>
              ) : (
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {cases.map((c) => {
                    const isClickable = c.status === "done" || c.status === "reviewing";
                    const tone = STATUS_TONE[c.status] ?? STATUS_TONE.draft;
                    const onClick = () => {
                      if (c.status === "done") {
                        window.location.hash = `#admin/archive/${c.id}`;
                      } else if (c.status === "reviewing" || c.status === "research" || c.status === "analyzing" || c.status === "received") {
                        window.location.hash = `#admin/work/${c.id}`;
                      }
                    };
                    return (
                      <div key={c.id}
                        onClick={isClickable || ["received","analyzing","research","reviewing"].includes(c.status) ? onClick : undefined}
                        style={{
                          display:'grid',gridTemplateColumns:'1fr auto auto',gap:10,alignItems:'center',
                          padding:'10px 12px',background:'#fafafa',borderRadius:10,
                          cursor: (isClickable || ["received","analyzing","research","reviewing"].includes(c.status)) ? 'pointer' : 'default',
                        }}>
                        <div style={{minWidth:0}}>
                          <div style={{fontSize:12,fontWeight:700,color:'#1a1a2e',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                            {c.child?.name ?? "(자녀 없음)"} · {c.concern_tags?.[0] ?? "(증상 미입력)"}
                          </div>
                          <div style={{fontSize:10,color:'#a0aec0',marginTop:2}}>
                            {c.sent_at ? `발행 ${formatDate(c.sent_at)}` : c.submitted_at ? `접수 ${formatDate(c.submitted_at)}` : `생성 ${formatDate(c.created_at)}`}
                            {c.plan_tier ? ` · ${c.plan_tier}` : ""}
                          </div>
                        </div>
                        <span style={{padding:'3px 8px',borderRadius:9999,fontSize:10,fontWeight:700,background:tone[0],color:tone[1]}}>
                          {STATUS_LABEL[c.status] ?? c.status}
                        </span>
                        <span style={{color:'#2c5f8d',fontSize:12}}>{isClickable || ["received","analyzing","research","reviewing"].includes(c.status) ? '→' : ''}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </Section>
          </div>

          <div style={{minWidth:0}}>
            {/* 분석권 패키지 */}
            <Section title={`🎟 분석권 ${activeCredits.length}개`}>
              {credits.length === 0 ? (
                <div style={{fontSize:12,color:'#718096'}}>지급된 분석권이 없어요.</div>
              ) : (
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {credits.map((p) => {
                    const expired = p.expires_at && new Date(p.expires_at).getTime() <= now;
                    return (
                      <div key={p.id} style={{padding:'8px 10px',background:expired?'#f7f7f7':'#fafafa',borderRadius:8,opacity:expired?0.55:1}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                          <div style={{fontSize:11,fontWeight:700,color:'#1a1a2e'}}>
                            {p.package_type ?? '패키지'} · {p.credits_remaining}/{p.credits_total}회
                          </div>
                          {expired && <span style={{fontSize:9,color:'#a0aec0',fontWeight:700}}>만료</span>}
                        </div>
                        <div style={{fontSize:10,color:'#a0aec0',marginTop:2}}>
                          {p.expires_at ? `${formatDate(p.expires_at)}까지` : '만료 없음'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Section>

            {/* 결제 내역 */}
            <Section title={`💳 결제 ${purchases.length}건`}>
              {purchases.length === 0 ? (
                <div style={{fontSize:12,color:'#718096'}}>결제 내역이 없어요.</div>
              ) : (
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {purchases.slice(0, 10).map((p) => {
                    const tone = PURCHASE_STATUS_TONE[p.status] ?? PURCHASE_STATUS_TONE.pending;
                    return (
                      <div key={p.id} style={{padding:'8px 10px',background:'#fafafa',borderRadius:8}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                          <div style={{fontSize:11,fontWeight:700,color:'#1a1a2e'}}>
                            {formatKRW(p.amount_won)}
                            <span style={{color:'#718096',fontWeight:500,marginLeft:6}}>· {p.plan_type}</span>
                          </div>
                          <span style={{padding:'2px 6px',borderRadius:9999,fontSize:9,fontWeight:700,background:tone[0],color:tone[1]}}>
                            {PURCHASE_STATUS_LABEL[p.status] ?? p.status}
                          </span>
                        </div>
                        <div style={{fontSize:10,color:'#a0aec0',marginTop:2}}>
                          {p.paid_at ? `결제 ${formatDate(p.paid_at)}` : `생성 ${formatDate(p.created_at)}`}
                          {p.payment_provider ? ` · ${p.payment_provider}` : ''}
                        </div>
                      </div>
                    );
                  })}
                  {purchases.length > 10 && (
                    <div style={{fontSize:10,color:'#a0aec0',textAlign:'center',padding:'4px 0'}}>
                      + {purchases.length - 10}건 더
                    </div>
                  )}
                </div>
              )}
            </Section>

            {/* 최근 알림 */}
            <Section title={`🔔 최근 알림 ${notifications.length}건`}>
              {notifications.length === 0 ? (
                <div style={{fontSize:12,color:'#718096'}}>알림 내역이 없어요.</div>
              ) : (
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {notifications.map((n) => (
                    <div key={n.id} style={{padding:'8px 10px',background:n.read_at?'#fafafa':'#fff8e1',borderRadius:8,
                      border:n.read_at?'none':'1px solid #ffd54f'}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',gap:8}}>
                        <div style={{fontSize:11,fontWeight:700,color:'#1a1a2e',minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                          {n.title}
                        </div>
                        <div style={{fontSize:9,color:'#a0aec0',whiteSpace:'nowrap'}}>{formatDateTime(n.created_at)}</div>
                      </div>
                      {n.body && (
                        <div style={{fontSize:10,color:'#4a5568',marginTop:2,lineHeight:1.5}}>{n.body}</div>
                      )}
                      <div style={{fontSize:9,color:n.read_at?'#a0aec0':'#9a3412',marginTop:3,fontWeight:600}}>
                        {n.read_at ? `읽음 ${formatDateTime(n.read_at)}` : '안 읽음'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{background:'#fff',borderRadius:14,padding:'14px 16px',boxShadow:'var(--shadow-sm)',marginBottom:12}}>
      <div style={{fontSize:11,fontWeight:800,color:'#4a5568',textTransform:'uppercase',letterSpacing:.4,marginBottom:10}}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, sub, tone }) {
  return (
    <div style={{background:'#fff',borderRadius:14,padding:'16px 18px',boxShadow:'var(--shadow-sm)'}}>
      <div style={{fontSize:11,color:'#718096',fontWeight:700,textTransform:'uppercase',letterSpacing:.4}}>{label}</div>
      <div style={{fontSize:24,fontWeight:800,color:tone,marginTop:4,letterSpacing:-0.5,fontVariantNumeric:'tabular-nums'}}>
        {value}
      </div>
      {sub && <div style={{fontSize:11,color:'#718096',marginTop:2}}>{sub}</div>}
    </div>
  );
}
